import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { t } from 'i18next';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';
import {
    DURATION_OPERATORS_WITHIN_AFTER,
    DURATION_OPERATORS_WITHIN_BEFORE_AFTER,
} from '@/data/Marketing/marketingDurationOperators';
import DaySelector from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
    type DurationCompositeSource,
} from './durationPeriodUtils';

interface DaysRangeInputProps {
    value: number[] | number | null;
    conditionInstanceId: string;
    isBooking?: boolean;
    operator?: string;
    compositeValues?: DurationCompositeSource & { operator?: string };
}

const validationSchema = Yup.object().shape({
    duration: Yup.object().shape({
        hours: Yup.number().test('required', t('Marketing.DaysRequired'), (val) => val !== null && val !== undefined),
    }),
});

const DaysRangeInput = ({
    value,
    conditionInstanceId,
    isBooking = false,
    operator,
    compositeValues,
}: DaysRangeInputProps) => {
    const { register } = usePlaygroundFormRegistry();
    const initialDuration = getDaySelectorValueFromComposite(compositeValues, {
        includeOperator: true,
        defaultOperator: operator ?? (isBooking ? 'BEFORE_HOURS' : 'AFTER_HOURS'),
    });
    if (
        !compositeValues?.days &&
        !compositeValues?.selectedDurationFormat &&
        (Array.isArray(value) ? value[0] : typeof value === 'number' ? value : null) !== null
    ) {
        const legacyAmount = Array.isArray(value) ? value[0] : (value as number);
        if (legacyAmount !== null && legacyAmount !== undefined) {
            initialDuration.hours = legacyAmount;
        }
    }

    const formik = useFormik({
        initialValues: {
            duration: initialDuration,
        },
        validationSchema,
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {},
    });

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                    formik.setTouched({ duration: DURATION_HOURS_TOUCHED });
                    return false;
                }
                return true;
            },
            getValues: () => buildCompositeFromDaySelectorValue(formik.values.duration),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, register]);

    return (
        <DaySelector
            label={isBooking ? t('Marketing.SelectTimePeriod') : t('Marketing.SelectDaysRange')}
            showOperator
            operatorOptions={isBooking ? DURATION_OPERATORS_WITHIN_BEFORE_AFTER : DURATION_OPERATORS_WITHIN_AFTER}
            value={formik.values.duration}
            onChange={(next) => formik.setFieldValue('duration', next)}
            summaryNoteKey={
                isBooking ? 'Marketing.DaySelectorNoteActiveBooking' : 'Marketing.DaySelectorNoteNewCustomer'
            }
            showError={Boolean(
                formik.touched.duration && (formik.errors.duration as { hours?: string } | undefined)?.hours,
            )}
            error={
                typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                    ? (formik.errors.duration as { hours?: string }).hours
                    : t('Marketing.DaysRequired')
            }
        />
    );
};

export default DaysRangeInput;
